import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import MKBox from 'components/MaterialKit/MKBox';
import MKTypography from 'components/MaterialKit/MKTypography';
import { useNavigate } from 'react-router-dom';
import { handleErrorResponse } from 'utils/general';
import { Grid } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import DoneIcon from '@mui/icons-material/Done';
import EditIcon from '@mui/icons-material/Edit';
import { Formik } from 'formik';
import InputField from 'components/InputField';
import { getFileData } from 'utils/file';
import { createBigFile } from 'api/files';
import get from 'lodash/get';
import words from 'lodash/words';
import cloneDeep from 'lodash/cloneDeep';
import { updatePassword, updateUser } from 'api/users';
import { withLoading } from 'utils/hoc';
import { useAuth } from 'contexts/auth';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Select from 'components/Select';
import phoneOptionsTemplate from 'components/Select/phone_options.json';
import { splitPhoneNumber } from 'utils/phone';

const DEFAULT_MENU_HEADER_HEIGHT = 100;

const EditProfileSection = ({
  setLoading,
  header_background_color,
  default_user_profile_image_url,
  font_color,
  ...props }) => {
  const navigate = useNavigate();
  const { auth, setAuth } = useAuth();
  const [user, setUser] = useState();
  const [imageFile, setImageFile] = useState();
  const [imageFileData, setImageFileData] = useState();
  const formikRef = useRef();
  const fileInputRef = useRef(null);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [phoneOptions, setPhoneOptions] = useState(cloneDeep(phoneOptionsTemplate));

  useEffect(() => {
    if (auth?.user) {
      setUser(auth.user);
    }
  }, [auth.user]);

  const getFormValidation = (values) => {
    const { display_name } = values;
    const errors = {};
    if (!display_name) {
      errors.display_name = 'Name is required';
    }
    return errors;
  };

  const initialValues = useMemo(() => ({
    display_name: user?.display_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
  }), [user]);

  const handleChangeFile = useCallback(async (event) => {
    const binaryData = [];
    binaryData.push(event.target.files[0]);
    const file = window.URL.createObjectURL(new Blob(binaryData, { type: 'image/*' }))
    setImageFile(file);
    const res = await getFileData(event.target.files[0]);
    setImageFileData(res);
  }, []);

  const handleImageUploadClick = () => {
    fileInputRef.current?.click();
  };

  const onUploadFile = useCallback(() => {
    if (imageFileData) {
      const fileBody = { ...imageFileData };
      const { file_url } = imageFileData;
      const type = get(file_url, 'type');
      fileBody.$term_mime_type = type;
      return createBigFile(fileBody)
        .then(({ data }) => {
          setImageFileData(null);
          return data.file_id;
        })
        .catch((err) => {
          handleErrorResponse(err, setAuth);
        });
    }
  }, [imageFileData, setAuth]);

  const updateUserToApi = useCallback((formik, fileId) => {
    const { values, setFieldError } = formik;
    const updateBody = {
      display_name: values.display_name,
      phone: values.phone,
      ...(fileId && { profile_image: fileId }),
    };
    return updateUser(values.email, updateBody)
      .then(() => {
        let passwordPromise;
        if (values.password) {
          passwordPromise = updatePassword(values.email, { password: values.password });
        }
        return Promise.resolve(passwordPromise)
          .then(() => {
            alert('Profile Updated Successfully');
            window.location.reload();
          });
      })
      .catch((err) => {
        setFieldError('form', err.message);
      });
  }, []);

  const onSubmit = useCallback(() => {
    if (formikRef.current) {
      setLoading(true);
      const uploadFilePromise = onUploadFile();
      Promise.resolve(uploadFilePromise)
        .then((fileId) => {
          return updateUserToApi(formikRef.current, fileId);
        })
        .catch((err) => {
          formikRef.current.setFieldError('form', err.message);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [onUploadFile, setLoading, updateUserToApi]);

  const userImageUrl = imageFile || auth?.user?.profile_image?.file_url;

  return (
    <MKBox>
      <MKBox
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        alignItems="center"
        bgColor="#eaeffa"
        // bgColor={secondary_header_background_color}
        sx={{
          minHeight: DEFAULT_MENU_HEADER_HEIGHT,
          padding: '1rem',
        }}
      >
        <MKBox display="flex" width="100%" justifyContent="space-between" alignItems="center">
          <MKBox display="flex" flex="1" justifyContent="flex-start">
            <ArrowBackIosIcon fontSize="medium" onClick={() => navigate(-1)} style={{ cursor: 'pointer', color: font_color }} />
          </MKBox>
          <MKBox display="flex" flex="2" justifyContent="center">
            <MKTypography color={font_color}>Edit Profile</MKTypography>
          </MKBox>
          <MKBox display="flex" flex="1" justifyContent="flex-end">
            <DoneIcon fontSize="large" onClick={onSubmit} style={{ cursor: 'pointer', color: font_color }} />
          </MKBox>
        </MKBox>
        <MKBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="150px"
          height="150px"
          minWidth="150px"
          sx={{
            position: 'relative',
            flexShrink: 0,
            borderRadius: '50%',
            backgroundImage: `url(${userImageUrl || default_user_profile_image_url})`,
            backgroundPosition: 'center',
            // a wide default logo has to fit inside the circle; a portrait fills it
            backgroundSize: userImageUrl ? 'cover' : 'contain',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <MKBox onClick={handleImageUploadClick} display="flex" padding="5px" alignItems="center" style={{ cursor: 'pointer', borderRadius: '50%', position: 'absolute', bottom: 0, right: 0, zIndex: 2, backgroundColor: 'yellow' }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleChangeFile}
            />
            <EditIcon fontSize="small" style={{ color: font_color }} />
          </MKBox>
        </MKBox>
      </MKBox>
      <MKBox padding={2}>
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validate={getFormValidation}
          enableReinitialize
          {...props}
        >
          {({ handleChange, handleBlur, handleSubmit, setFieldValue, errors, values, touched, isSubmitting, dirty }) => {
            const { phone } = values;
            const [ phoneCountry, phoneRemaining ] = splitPhoneNumber(phone);
            return (
              <MKBox component="form" role="form">
                <Grid container spacing={1}>
                  <Grid container item xs={12}>
                    <MKTypography color={font_color} fontSize="18px">
                      Name
                    </MKTypography>
                    <InputField
                      name="display_name"
                      label=""
                      value={values.display_name}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      error={touched.display_name && !!errors.display_name}
                    />
                    <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                      {(touched.display_name && !!errors.display_name) ? errors.display_name : '\xa0'}
                    </MKTypography>
                  </Grid>
                  <Grid container item xs={12}>
                    <MKTypography color={font_color} fontSize="18px">
                      Email
                    </MKTypography>
                    <InputField
                      name="email"
                      label=""
                      value={values.email}
                      disabled
                    />
                    <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                      {'\xa0'}
                    </MKTypography>
                  </Grid>
                  <Grid container item xs={12} flexDirection="column">
                    <MKTypography color={font_color} fontSize="18px">
                      Password
                    </MKTypography>
                    <MKBox display="flex" flex={1} alignItems="center">
                      <InputField
                        name="password"
                        type={passwordVisible ? 'text' : 'password'}
                        label=""
                        value={values.password}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        error={touched.password && !!errors.password}
                      />
                      <VisibilityIcon color="black" style={{ marginLeft: '1rem', cursor: 'pointer' }} onClick={() => setPasswordVisible(!passwordVisible)} />
                    </MKBox>
                    <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                      {'\xa0'}
                    </MKTypography>
                  </Grid>

                  <Grid container item xs={12}>
                    <MKTypography color={font_color} fontSize="18px">
                      Phone country (Whatsapp)
                    </MKTypography>
                    <Select
                      name="phone_country"
                      options={phoneOptions}
                      value={phoneCountry}
                      onChange={(newValue) => {
                        setFieldValue('phone', `${newValue}${phoneRemaining}`);
                      }}
                      handleBlur={handleBlur}
                      error={touched.value && !!errors.value}
                    />
                  </Grid>
                  <Grid container item xs={12}>
                    <MKTypography color={font_color} fontSize="18px">
                      Phone (Whatsapp)
                    </MKTypography>
                    <InputField
                      name="phone"
                      label=""
                      value={phone}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      error={touched.phone && !!errors.phone}
                    />
                  </Grid>
                </Grid>
                <Grid container mb={5}>
                  <Grid item xs={12}>
                    <MKTypography variant="caption" color="error">
                      {errors.form}
                      &nbsp;
                    </MKTypography>
                  </Grid>
                </Grid>
              </MKBox>
            );
          }}
        </Formik>
      </MKBox>
    </MKBox>
  );
};

EditProfileSection.propTypes = {
  setLoading: PropTypes.func,
  header_background_color: PropTypes.string,
  default_user_profile_image_url: PropTypes.string,
  font_color: PropTypes.string,
};

EditProfileSection.defaultProps = {
  setLoading: null,
  header_background_color: '',
  default_user_profile_image_url: '',
  font_color: '',
};

export default withLoading(EditProfileSection);
